import { Button, Classes, Spinner, SpinnerSize } from '@blueprintjs/core';
import {
  payment_remark_hooks,
  useLoanApplication,
} from '@axo/shared/data-access/hooks';
import { AceCardSection } from '../AceCard';
import { classNames } from '@axo/shared/util/dom';
import styles from './PaymentRemarkSection.module.css';
import { formatDatetime } from '../../utils/formatDate';
import { PaymentRemarkTag } from './PaymentRemarkTag/PaymentRemarkTag';
import { ValueWarning } from '../ApplicationData/ValueWarning/ValueWarning.tsx';
import { AppToaster } from '../AppToaster';
import { useMemo } from 'react';
import { Broker } from '@axo/shared/data-access/types/broker';

interface IPaymentRemarkSection {
  applicationID: string;
  personID: string;
}

export function Section({ applicationID, personID }: IPaymentRemarkSection) {
  const { data: applicationData } = useLoanApplication(applicationID);

  const paymentRemarks = payment_remark_hooks.usePaymentRemarks(personID);
  const trigger = payment_remark_hooks.useTriggerCreditCheck();
  const latest = paymentRemarks.data?.sort(
    (a, b) => +new Date(b.UpdatedAt) - +new Date(a.UpdatedAt),
  )[0];

  const numberOfRemarks = useMemo(() => {
    if (!latest) return;
    if (latest.NumberOfRemarks > 0) return latest.NumberOfRemarks;
    if (latest.Status === 'OK') return latest.NumberOfRemarks;
    return undefined;
  }, [latest]);

  const handleTrigger = () => {
    if (personID && applicationData) {
      trigger
        .mutateAsync({
          personID,
          marketCountry: applicationData?.MarketCountry,
          loanApplicationID: applicationID,
          broker: applicationData.Meta?.Broker ?? Broker.Axo,
        })
        .then(() => {
          AppToaster.then((toaster) =>
            toaster.show({
              intent: 'success',
              message: `New credit check triggered. Will update when complete.`,
            }),
          );
        });
    }
  };

  const dateIsOlderThan9Days = (date: string) => {
    const differenceInDays =
      (new Date().getTime() - new Date(date).getTime()) / (1000 * 3600 * 24);
    return differenceInDays > 9;
  };

  return (
    <AceCardSection
      id={`PaymentRemark.${personID}`}
      title={<span className={styles.title}>Payment remarks</span>}
      tag={<PaymentRemarkTag numberOfRemarks={numberOfRemarks} />}
    >
      {paymentRemarks.isLoading && <Spinner size={SpinnerSize.SMALL} />}
      {latest && (
        <div className={classNames(Classes.TEXT_SMALL, Classes.RUNNING_TEXT)}>
          <div className={styles.paymentRemarkInfo}>
            {latest.Status && latest.Status !== 'OK' && (
              <>
                <span className={styles.label}>Error:</span>
                <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
                  {latest.Status}
                </span>
              </>
            )}
            <span className={styles.label}>Number of remarks:</span>
            <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
              {numberOfRemarks ?? 'Unknown'}
            </span>
            <span className={styles.label}>Score:</span>
            <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
              {latest.Score}
            </span>
            <span className={styles.label}>Date:</span>
            <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
              {dateIsOlderThan9Days(latest.CreatedAt) ? (
                <ValueWarning
                  value={formatDatetime(latest.CreatedAt)}
                  content="Date is older than 9 days"
                />
              ) : (
                formatDatetime(latest.CreatedAt)
              )}
            </span>

            <span className={styles.label}>Credit rating class:</span>
            <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
              {latest.CreditRatingClass}
            </span>
            <span className={styles.label}>Taxed yearly income:</span>
            <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
              {latest.GrossIncomeYearlyDeclared}
            </span>
            <span className={styles.label}>Taxed updated at:</span>
            <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
              {formatDatetime(latest.AssessmentSubscriptionDate, true)}
            </span>
            <span className={styles.label}>Bailiff Debt:</span>
            <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
              {latest.OutstandingAmount}
            </span>
            <span className={styles.label}>Surplus from Capital:</span>
            <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
              {latest.CapitalSurplus}
            </span>
            <span className={styles.label}>Capital deficit:</span>
            <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
              {latest.CapitalDeficit}
            </span>
            <span className={styles.label}>Number of credit checks:</span>
            <span className={classNames(styles.value, Classes.TEXT_MUTED)}>
              {latest.NumberOfInquiries ?? 0}
            </span>
          </div>
          {latest.PaymentRemarkInquiries &&
            latest.PaymentRemarkInquiries.length > 0 && (
              <div className={styles.tableContainer}>
                <span className={styles.label}>Latest credit checks:</span>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latest.PaymentRemarkInquiries.map((inquiry) => (
                        <tr key={inquiry.ID}>
                          <td>
                            {inquiry.InquirerName ? inquiry.InquirerName : '-'}
                          </td>
                          <td>{formatDatetime(inquiry.InquiryTime, true)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </div>
      )}
      <Button
        onClick={handleTrigger}
        small
        icon="automatic-updates"
        className={styles.triggerButton}
        disabled={!applicationData || !personID || trigger.isLoading}
      >
        New credit check
      </Button>
    </AceCardSection>
  );
}
